import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["occurrence", "ariaLabelledBy", "className", "variant", "id", "style"];
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import { useId } from '@base-ui/utils/useId';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { TimelineGrid } from '@mui/x-scheduler-internals-premium/timeline-grid';
import { schedulerEventSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventTimelinePremiumStoreContext } from '@mui/x-scheduler-internals-premium/use-event-timeline-premium-store-context';
import { EventDragPreview, getPaletteVariants } from '@mui/x-scheduler/internals';
import { useEventTimelinePremiumStyledContext } from "../../EventTimelinePremiumStyledContext.mjs";
import { eventTimelinePremiumClasses } from "../../eventTimelinePremiumClasses.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ARROW_DEPTH = 8; // px - depth of the chevron point
const LEFT_ARROW_CLIP = `polygon(${ARROW_DEPTH}px 0, 100% 0, 100% 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;
const RIGHT_ARROW_CLIP = `polygon(0 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, 0 100%)`;
const BOTH_ARROWS_CLIP = `polygon(${ARROW_DEPTH}px 0, calc(100% - ${ARROW_DEPTH}px) 0, 100% 50%, calc(100% - ${ARROW_DEPTH}px) 100%, ${ARROW_DEPTH}px 100%, 0 50%)`;
const EventTimelinePremiumEventRoot = styled('div', {
  name: 'MuiEventTimeline',
  slot: 'Event'
})(({
  theme
}) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'var(--event-surface-subtle)',
  color: 'var(--event-on-surface-subtle-primary)',
  padding: theme.spacing(0.5, 1),
  position: 'relative',
  width: 'var(--width)',
  height: `calc(${theme.typography.body2.lineHeight}em + ${theme.spacing(1)})`,
  marginLeft: 'var(--x-position)',
  gridRow: 'var(--row-index, 1)',
  gridColumn: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  '&[data-dragging], &[data-resizing]': {
    opacity: 0.5
  },
  '&:hover': {
    backgroundColor: 'var(--event-surface-subtle-hover)'
  },
  '&[data-editing]': {
    backgroundColor: 'var(--event-surface-selected)',
    color: 'var(--event-on-surface-selected)',
    '&:hover': {
      backgroundColor: 'var(--event-surface-selected-hover)'
    },
    '&::before': {
      background: 'var(--event-surface-selected)'
    }
  },
  [`&:hover .${eventTimelinePremiumClasses.eventResizeHandler}`]: {
    opacity: 1
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: 3,
    borderRadius: '4px 0 0 4px',
    background: 'var(--event-surface-accent)',
    pointerEvents: 'none'
  },
  '&[data-starting-before-edge]': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    clipPath: LEFT_ARROW_CLIP,
    paddingLeft: ARROW_DEPTH + 8,
    '&::before': {
      display: 'none'
    }
  },
  '&[data-ending-after-edge]': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    clipPath: RIGHT_ARROW_CLIP,
    paddingRight: ARROW_DEPTH + 8
  },
  '&[data-starting-before-edge][data-ending-after-edge]': {
    clipPath: BOTH_ARROWS_CLIP
  },
  variants: getPaletteVariants(theme)
}));
const EventTimelinePremiumEventLinesClamp = styled('span', {
  name: 'MuiEventTimeline',
  slot: 'EventLinesClamp'
})({
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
});
const EventTimelinePremiumEventRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventTimeline',
  slot: 'EventRecurringIcon'
})({
  flexShrink: 0,
  fontSize: '1rem'
});
const EventTimelinePremiumEventResizeHandler = styled(TimelineGrid.EventResizeHandler, {
  name: 'MuiEventTimeline',
  slot: 'EventResizeHandler'
})({
  position: 'absolute',
  width: 4,
  top: 0,
  bottom: 0,
  zIndex: 3,
  cursor: 'ew-resize',
  opacity: 0,
  '&[data-start]': {
    left: 0
  },
  '&[data-end]': {
    right: 0
  }
});
export const EventTimelinePremiumEvent = /*#__PURE__*/React.forwardRef(function EventTimelinePremiumEvent(props, forwardedRef) {
  const {
      occurrence,
      ariaLabelledBy,
      className,
      variant,
      id: idProp
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);

  // Context hooks
  const store = useEventTimelinePremiumStoreContext();
  const {
    classes
  } = useEventTimelinePremiumStyledContext();
  // Selector hooks
  const isDraggable = useStore(store, schedulerEventSelectors.isDraggable, occurrence.id);
  const isStartResizable = useStore(store, schedulerEventSelectors.isResizable, occurrence.id, 'start');
  const isEndResizable = useStore(store, schedulerEventSelectors.isResizable, occurrence.id, 'end');
  const color = useStore(store, schedulerEventSelectors.color, occurrence.id);
  const isRecurring = useStore(store, schedulerEventSelectors.isRecurring, occurrence.id);

  // Feature hooks
  const id = useId(idProp);
  const sharedProps = _extends({
    id,
    start: occurrence.displayTimezone.start,
    end: occurrence.displayTimezone.end,
    ref: forwardedRef,
    'aria-labelledby': `${ariaLabelledBy} ${id}`,
    className: clsx(className, occurrence.className),
    style: {
      '--number-of-lines': 1,
      '--row-index': occurrence.position.firstIndex
    },
    'data-palette': color
  }, other);
  if (variant === 'placeholder') {
    return /*#__PURE__*/_jsxs(TimelineGrid.EventPlaceholder, _extends({
      render: /*#__PURE__*/_jsx(EventTimelinePremiumEventRoot, {}),
      "aria-hidden": true
    }, sharedProps, {
      className: clsx(sharedProps.className, classes.eventPlaceholder),
      children: [/*#__PURE__*/_jsx(EventTimelinePremiumEventLinesClamp, {
        className: classes.eventLinesClamp,
        children: occurrence.title
      }), isRecurring && /*#__PURE__*/_jsx(EventTimelinePremiumEventRecurringIcon, {
        className: classes.eventRecurringIcon,
        "aria-hidden": "true"
      })]
    }));
  }
  return /*#__PURE__*/_jsxs(TimelineGrid.Event, _extends({
    render: /*#__PURE__*/_jsx(EventTimelinePremiumEventRoot, {}),
    isDraggable: isDraggable,
    eventId: occurrence.id,
    occurrenceKey: occurrence.key,
    renderDragPreview: parameters => /*#__PURE__*/_jsx(EventDragPreview, _extends({}, parameters))
  }, sharedProps, {
    className: clsx(sharedProps.className, classes.event),
    children: [isStartResizable && /*#__PURE__*/_jsx(EventTimelinePremiumEventResizeHandler, {
      side: "start",
      className: classes.eventResizeHandler
    }), /*#__PURE__*/_jsx(EventTimelinePremiumEventLinesClamp, {
      className: classes.eventLinesClamp,
      children: occurrence.title
    }), isRecurring && /*#__PURE__*/_jsx(EventTimelinePremiumEventRecurringIcon, {
      className: classes.eventRecurringIcon,
      "aria-hidden": "true"
    }), isEndResizable && /*#__PURE__*/_jsx(EventTimelinePremiumEventResizeHandler, {
      side: "end",
      className: classes.eventResizeHandler
    })]
  }));
});
if (process.env.NODE_ENV !== "production") EventTimelinePremiumEvent.displayName = "EventTimelinePremiumEvent";